import { CSSProperties } from "react";

import { Controller, UseControllerProps } from "react-hook-form";
import Select, { StylesConfig } from "react-select";

import { DateTime } from "luxon";

import { operatorAvailabilityValuesByStatus } from "@/_constants/operator.ts";
import { SCvalues } from "@/_helpers/commonComponentsTypes.ts";

import { ErrorForm } from "./ErrorForm.tsx";
import { LabelForm, LabelFormProps } from "./LabelForm.tsx";


const colourStyles: StylesConfig<SCvalues, true> = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "#FAFAFA",
    borderColor: "#C5C5C5",
    minHeight: "38px",
    height: "auto",
  }),
  container: (styles) => ({
    ...styles,
    width: "100%",
    minHeight: "38px",
  }),
  option: (styles, { isDisabled, isSelected }) => ({
    ...styles,
    color: isSelected ? "#fff" : "#000",
    cursor: isDisabled ? "not-allowed" : "default",
    backgroundColor: isSelected ? "#641ABF" : "",
    ":hover": {
      ...styles[":active"],
      backgroundColor: "#641ABF",
      color: "#FFF",
    },
  }),
  multiValue: (styles) => ({
    ...styles,
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: "#FFF",
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: "#FFF",
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "#6c757d",
  }),
};

type SelectFormProps = {
  style?: CSSProperties;
  extraSelect?: JSX.Element | null;
  options: SCvalues[];
  isGroup?: boolean;
  isMulti?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  className?: string;
  icon?: React.ComponentType<any> | React.ReactElement;
} & UseControllerProps &
  LabelFormProps;

const groupOptionsByAvailability = (options: SCvalues[]) => {
  return Object.values(
    options.reduce(
      (acc, option) => {
        const { availabilityStatus } = option;
        const { label: status, color } = operatorAvailabilityValuesByStatus(
          availabilityStatus,
        ) || {
          label: "Inconnue",
          color: "",
        };

        if (!acc[status]) {
          acc[status] = { label: status, color, options: [] };
        }

        acc[status].options.push({ ...option, label: option.label });
        return acc;
      },
      {} as Record<
        string,
        { label: string; color: string; options: SCvalues[] }
      >,
    ),
  );
};

const SelectForm = ({
  style,
  control,
  defaultValue,
  isMulti,
  label,
  labelInfos,
  name,
  options,
  placeholder,
  extraSelect,
  isClearable,
  className,
  rules,
  isGroup = false,
  icon,
}: SelectFormProps) => {
  const noOptionsMessage = () => "Pas de résultats";
  const processedOptions = isGroup
    ? groupOptionsByAvailability(options)
    : options;

  return (
    <div
      style={style}
      className={className || style ? "form-input" : "form-input w-100"}
    >
      <LabelForm
        label={label}
        required={!!rules?.required}
        labelInfos={labelInfos}
        icon={icon}
      />
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field, fieldState }) => (
          <div className="d-flex flex-column select-wrapper">
            <div className="d-flex justify-content-center align-items-center w-100">
              {extraSelect}
              <Select
                classNamePrefix="react-select"
                isClearable={isClearable}
                styles={{
                  ...colourStyles,
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "100%",
                    minHeight: "38px",
                    marginBottom: "5px"
                  })
                }}
                onChange={(value) => field.onChange(value)}
                noOptionsMessage={noOptionsMessage}
                options={processedOptions}
                isMulti={isMulti ? true : undefined}
                placeholder={placeholder ?? " "}
                defaultValue={defaultValue}
                value={field.value}
                getOptionLabel={(option) =>
                  `${option.label} ${
                    option?.firstAvailability?.startDate
                      ? `(${DateTime.fromISO(option?.firstAvailability?.startDate).toFormat("dd/MM/yyyy - HH:mm")})`
                      : ""
                  }`
                }
              />
            </div>
            <ErrorForm error={fieldState.error} />
          </div>
        )}
        rules={rules}
      />
    </div>
  );
};

export default SelectForm;
